import { useEffect, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import {
  getResearch,
  isError,
  startSubjectResearch,
  type CoverageView,
  type ResearchDocument,
  type ResearchProgress,
  type ResearchSubject,
} from './api';
/*
 * What may be said about coverage is decided in a module a Node test can import, not here
 * (F-129). This screen renders sentences about whether a subject reached the plan, and the two
 * ways of getting that wrong are both silent — see `plan/coverage.ts`.
 */
import { reconcile, verdictsById } from './plan/coverage';
/*
 * What one subject row may say about itself, decided in a module a Node test can import (F-158).
 * The badge, the sources line and whether the actions are offered were all decided inline here,
 * which is to say in a file nothing type-checks — see that module's header for what each cost.
 */
import { subjectView } from './plan/subject';
/* One definition of "material is still arriving", shared with the screen that decides the step
   from it. See `stillComing` — this file's own `working` is one of the two copies it replaced. */
import { stillComing } from './plan/pulse';
/* What this section says when it has a run, and what it says when it has nothing (F-141). */
import { researchFace } from './plan/view';
/*
 * The live panel, shared with the plan screen (F-147). It is handed the progress this screen has
 * already polled rather than fetching its own — two pollers for one number on one screen is how
 * two panels come to disagree about it.
 */
import ResearchPulse from './ResearchPulse';
/* The uploader is several panels down this same screen, and it owns everything about uploading. */
import { goToUpload } from './panels';

/**
 * Research, while it is happening and after it has.
 *
 * **The panel exists because "reading up on it" for fifteen minutes is not a progress
 * indicator, it is a promise.** Research used to be one call that either produced a
 * twenty-thousand-word document or produced nothing, and until it landed there was one
 * sentence on the screen that said the same thing whether the pass was thirty seconds from
 * finishing or had died an hour ago.
 *
 * It is now an outline followed by waves of batched per-subject research (F-109), and this
 * shows that: the subjects appear within a minute or two, each with a sentence, and then fill
 * in one wave at a time. Everything on screen is read from a row a pass actually wrote — the
 * overall fraction is computed by the API, and each subject's state is its own row. Nothing
 * here advances on a timer, because a bar that moves for no reason teaches somebody that the
 * bar means nothing.
 *
 * ---
 *
 * **It is a section of the objective's own screen now, and not a screen of its own** (F-141).
 * It used to be the whole of `/research`, which meant it could poll for its own progress and
 * fetch its own coverage report without anything else on the page doing the same. Merged into
 * the plan, both of those would be a second request for an answer the screen already holds —
 * *"two pollers for one number on one screen is how two panels come to disagree about it"*, the
 * rule this component's own `ResearchPulse` import has carried since F-147, now applied to this
 * component. So progress and coverage arrive as props from `Objectives`, which polls once, and
 * what is left here is the rendering plus the one request nothing else wants: the documents.
 *
 * **And it says something true when it has nothing**, which it never had to before: a route
 * nobody navigated to could render an empty fragment, and a section sitting in the middle of
 * the plan cannot. `researchFace` in `plan/view.ts` is that decision, and it is there rather
 * than here because `npm test` does not type-check this file at all.
 */

/*
 * The price that used to be on this button is gone, and it is not coming back.
 *
 * Leo, 2026-09-15: *"the user should not know about budget, we will hide that in our business
 * model… they will pay a monthly flat fee so budget is our concern."* A learner reading "Go
 * deeper — $0.25" is being asked to do accounting on their own curriculum, per subject, which
 * is a decision they cannot make well and did not sign up to make at all.
 *
 * **What replaces it is scope, not silence.** A subject that will not be researched is still
 * said out loud on this screen, because a plan that is quietly incomplete is worse than one
 * that says which parts of it are not covered. What changed is the reason given: it is what
 * this plan reaches, not what it costs.
 */

/*
 * **Three tables stood here until F-158 and all three are gone**, because each of them was a
 * judgement made in a file `npm test` does not type-check:
 *
 * - `PILL`, which mapped all eight subject states onto a pill style. A researched subject no
 *   longer wears one at all; the four that do are decided by `badgeOf`, which returns the tone
 *   with the key so the two cannot drift apart.
 * - `KEEPS_ITS_STATE`, which chose between the coverage report's word for a subject and the
 *   row's own. There is no word to choose: the outcome vocabulary has left the screen.
 * - `DEEPENABLE`, five of the eight states, which is the set that made *"researched doesn't show
 *   those buttons"* true. Every subject is deepenable; see `actionsShown`.
 *
 * What replaces them is `plan/subject.ts`, which a Node test imports.
 */

export default function Research({
  objectiveId,
  progress,
  coverage,
  uploadsOnly,
  starting,
  stalled,
  onCheck,
  pulse,
  onChanged,
}: {
  objectiveId: string;
  /**
   * Where the research run has got to, polled once by `Objectives` and handed down (F-141).
   *
   * **This used to be polled here, and it must not be again.** Merged into the plan, the panel
   * above this one renders the same reading through `ResearchPulse`, the step it offers is
   * decided from it by `planStep`, and the screen's own poll loop is kept alive by it — four
   * readers of one number, which is exactly one number. `undefined` is "not read yet" and also
   * "no run exists"; both mean there is nothing of a run to show, which is all this file does
   * with it.
   */
  progress: ResearchProgress | undefined;
  /**
   * What happened to every researched subject, on the same terms and for the same reason.
   *
   * `Objectives` reads it from an effect keyed on the run's state, so it arrives when there is a
   * plan to compare against *or* a run to have verdicts from — which covers the `drafting`
   * objective whose pass failed, where the rows still carry an outcome derived from their state
   * and the two remedies F-129 promised. It is not read while material is still arriving:
   * `coverageFor` is four reads and is deliberately off the polling path, so during a run the
   * rows below label themselves from their own state rather than from a verdict. That is the
   * honest reading anyway — a subject still being read has not been asked to reach the plan yet.
   */
  coverage: CoverageView | undefined;
  /** `planFrom === 'uploads'`: this objective never researches, so say that rather than nothing. */
  uploadsOnly: boolean;
  /**
   * The learner has pressed *start research* and nothing has recorded it yet (F-144).
   *
   * Passed down so this section cannot contradict the panel above it during that window — see
   * `researchFace`, where the reasoning is.
   */
  starting: boolean;
  /**
   * The screen has stopped asking on its own, and this section is the one that says so.
   *
   * **The escape belongs to whichever panel is describing the wait**, and for a research run that
   * is this one. `Research.tsx` used to own a poll and a "check now" of its own; handing the poll
   * to `Objectives` took the button with it, and the three that remained are gated on things that
   * are not "a run is in flight" — so a `scoped` objective deepening a subject (no *look again*
   * panel, by F-118's gate) and any objective whose thirty-minute research lock had been stripped
   * as stale had no button anywhere, in front of a wave the pipeline runs for up to 109 minutes.
   *
   * `false` while the step panel above is showing its own copy: they start the same poll, and two
   * identical buttons is the duplication merging the screens made possible.
   */
  stalled: boolean;
  /** Resume the screen's poll. The same `checkAgain` every other wait on this screen offers. */
  onCheck: () => void;
  /**
   * Whether this section draws the live panel, or the step panel above it already has.
   *
   * See `ownsPulse` in `plan/view.ts`. Two `ResearchPulse`s on one screen is two lists of the
   * same twelve subjects — the disagreement F-129 exists to have ended — and before F-141 the
   * two panels could not meet because they were on two different routes.
   */
  pulse: boolean;
  /**
   * Reload the objective and its run.
   *
   * `Objectives` owns every fetch this screen makes now, so the two controls here that change
   * something on the server — a deeper pass, and the refusals that mean this screen is arguing
   * with a row it has not read recently — ask it to look again rather than reading for
   * themselves. Without it a deeper pass would start and nothing on the page would move until
   * the next poll happened to notice.
   */
  onChanged: () => void;
}) {
  const { t, locale } = useI18n();
  const [documents, setDocuments] = useState<ResearchDocument[] | undefined>();
  const [open, setOpen] = useState(false);
  const [deepening, setDeepening] = useState<string | undefined>();
  const [problem, setProblem] = useState<string | undefined>();

  /*
   * The documents, re-read when the run state moves — which is what brings each wave's material
   * in as it lands.
   *
   * It is the one request this component still makes, and nothing else on the screen wants it.
   * The coverage report used to ride on this same dependency and now belongs to `Objectives`,
   * which is the component that knows whether there is a plan for it to be compared against.
   */
  useEffect(() => {
    let stale = false;
    void getResearch(objectiveId).then((result) => {
      // Guarded because switching objectives remounts this with a new id while the previous
      // request is still in flight — without it the old objective's research can land on the
      // new one's screen.
      if (stale) return;
      /*
       * A failed read stays `undefined` rather than becoming an empty list, and the difference is
       * a sentence this section would otherwise say untruthfully.
       *
       * `[]` is a defined answer, so `researchFace` reads it as "asked, and there is nothing" and
       * renders *nothing has been read about this yet* — over an objective with twelve researched
       * subjects, whenever this request and the progress request both fail. On a route of its own
       * an unreadable list rendered nothing and claimed nothing; as a section with a heading it
       * has to keep that property deliberately.
       */
      setDocuments(isError(result) ? undefined : result.documents);
    });
    return () => {
      stale = true;
    };
  }, [objectiveId, progress?.run.state]);

  async function goDeeper(subject: ResearchSubject): Promise<void> {
    if (!window.confirm(t('res.deeper.confirm'))) return;
    setDeepening(subject.subjectId);
    setProblem(undefined);
    const result = await startSubjectResearch(objectiveId, subject.subjectId);
    if (isError(result)) {
      setDeepening(undefined);
      setProblem(explain(result.error));
      // Both refusals mean this screen is arguing with a row it has not read recently.
      // Reloading answers them; retrying against the same stale reading would not.
      onChanged();
      return;
    }
    setDeepening(undefined);
    /*
     * A new run on the same objective, so the screen's poll loop — which stops when nothing is
     * in flight — has to be sent round again. Without it the panel freezes on one reading while
     * a batch the learner just paid for runs invisibly.
     *
     * It is the parent's loop now rather than one of this component's own (F-141), and that is
     * strictly better here: the loop it restarts is the same one the step panel, the build offer
     * and the fold are all watching, so a deeper pass moves every one of them rather than only
     * the list it was started from.
     */
    onChanged();
  }

  function explain(error: string): string {
    if (error === 'already_running') return t('res.error.running');
    if (error === 'not_ready') return t('res.error.notReady');
    // Not an error: the plan builds itself once enough research has landed.
    if (error === 'still_researching') return t('plan.error.stillResearching');
    if (error === 'no_subject') return t('res.error.noSubject');
    /*
     * The two `startSubjectResearch` can refuse with that this screen had no words for (F-158).
     *
     * **It matters more now than it did**, and that is why a review caught it here: the deeper
     * pass used to be offered on five of eight states and on four subjects of twelve, and it is
     * now on every row. `no_research` is an objective the learner switched to their own pages,
     * and `research_grant_spent` is a month's passes used up — both are true, both are about a
     * choice or a budget rather than a fault, and both were arriving as *"something went wrong"*.
     * `Objectives.tsx` has had copy for them since F-118 and F-131; this is the same two
     * sentences, not new ones.
     */
    if (error === 'no_research') return t('obj.error.noResearch');
    if (error === 'research_grant_spent') return t('obj.error.researchGrantSpent');
    return t('res.error.generic');
  }

  const run = progress?.run;
  const subjects = progress?.subjects ?? [];
  /* `stillComing` in `plan/pulse.ts` and not a third copy of the comparison — its own docstring
     names this line as one of the two it replaced. */
  const working = stillComing(run?.state);
  const deep = subjects.filter((subject) => subject.state === 'deep').length;
  /*
   * What happened to each subject, and the two numbers the learner is comparing.
   *
   * Hung off the progress rows by subject id rather than rendered as a list of its own: the
   * report and the progress panel are about the same twelve subjects, and a second list beside
   * the first is how "twelve subjects" and "five subjects" became two unexplained numbers in
   * the first place. A subject still being researched has no verdict, which is correct — it has
   * not been asked to reach the plan yet.
   */
  const verdicts = verdictsById(coverage);
  const summary = reconcile(coverage);
  /* Said only when there is a plan to compare against. Before one is built there is nothing to
     reconcile, and a line reading "0 in your plan" would report an ordinary state as a loss. */
  const comparable = coverage !== undefined && coverage.planNodes > 0 && summary.researched > 0;

  const when = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  /*
   * Which of the five things this section has to say, decided in a module a test can read.
   *
   * It matters here and did not on a route of its own: a section sitting between the plan and
   * the uploader cannot render an empty fragment and leave a heading promising something behind
   * it. See `researchFace`.
   */
  const face = researchFace({
    uploadsOnly,
    starting,
    hasRun: Boolean(run),
    running: working,
    documents: documents?.length,
  });

  return (
    <>
      {/*
       * Nothing was looked up, and why, rather than a heading over an empty section (F-141).
       *
       * `unread` says nothing on purpose — it is the half-second before either request answers,
       * and "nothing has been read about this yet" is a claim, not a loading state.
       */}
      {(face === 'none' || face === 'off') && (
        <section className="panel">
          <p className="muted">{t(face === 'off' ? 'res.empty.uploads' : 'res.empty.none')}</p>
        </section>
      )}
      {run && (
        <section className="panel">
          <h2>{t('res.progress.title')}</h2>

          {/* A native `<progress>`, not a div pretending to be one. It carries the value to a
              screen reader without any ARIA of ours, and it cannot drift from the number
              beside it because there is only one number. */}
          <progress value={progress?.overall ?? 0} max={1} />
          <p className="muted small">
            {t(`res.progress.${run.state}` as CopyKey)}
            {subjects.length > 0
              ? ` · ${t('res.progress.of')
                  .replace('{done}', String(deep))
                  .replace('{total}', String(subjects.length))}`
              : ''}
          </p>

          {/* The wait, said out loud rather than hidden behind a spinner. A learner who does
              not know a batch can take an hour concludes after ten minutes that it is broken,
              and the honest answer is that the push is what brings them back. */}
          {run.state === 'deepening' && <p className="muted small">{t('res.progress.slow')}</p>}

          {/*
            What is arriving, and a line of what it says (F-147, F-149).

            The bars are off here and only here: every subject is listed below with its own bar,
            its own state and its own two remedies, and a second list of the same twelve subjects
            above it is exactly the duplication F-129 was written to end. What this adds to that
            list is the shape of the run — how many are open, how the material arrived over the
            hour, and a paragraph of what the learner is about to be taught.

            **And not at all while the step panel above is showing one** (F-141). The two panels
            used to be on two routes and could never meet; on one screen a second copy of the
            counts and the arrival curve is the disagreement F-129 exists to have ended. See
            `ownsPulse`.
          */}
          {pulse && (
            <ResearchPulse
              objectiveId={objectiveId}
              progress={progress}
              starting={false}
              bars={false}
            />
          )}

          {/*
            The owner's question, answered where he asked it (F-129): *"I have 9 out of 12
            researched and my plan is just 5 subjects."* Both numbers come from one answer, so
            they cannot disagree, and the list below says what happened to each of the ones that
            did not make it.

            When the plan predates the join there is no comparison to draw and the sentence says
            so instead of drawing a false one — that is the state the two objectives being taught
            from today are in.
          */}
          {comparable && summary.known && (
            <p className="muted small">
              {t('cov.summary')
                .replace('{researched}', String(summary.researched))
                .replace('{inPlan}', String(summary.inPlan))}
              {summary.missing > 0
                ? ` · ${t('cov.summary.missing').replace('{missing}', String(summary.missing))}`
                : ''}
            </p>
          )}
          {comparable && !summary.known && (
            <p className="muted small">{t('cov.summary.unknown')}</p>
          )}
          {comparable && summary.pending > 0 && (
            <p className="muted small">
              {t('cov.summary.pending').replace('{pending}', String(summary.pending))}
            </p>
          )}

          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}

          {/*
           * The "this page has stopped watching" escape, offered from the panel that is describing
           * the wait rather than from a panel that happens to be about research.
           *
           * The poll it resumes is the screen's, not this component's — see `stalled` and
           * `onCheck`. `working` is the same guard the deleted version carried: a run that has
           * finished is not a wait, and a button offering to look again at it says the opposite.
           */}
          {stalled && working && (
            <>
              <p className="muted">{t('obj.wait.slow')}</p>
              <div className="actions">
                <button type="button" className="button" onClick={onCheck}>
                  {t('obj.wait.check')}
                </button>
              </div>
            </>
          )}

          <ul className="gaps">
            {subjects.map((subject) => {
              const verdict = verdicts.get(subject.subjectId);
              /*
               * Everything this row is allowed to say about the subject, decided in
               * `plan/subject.ts` where a Node test can see it (F-158).
               *
               * **Three judgements used to be made here, in a file `npm test` does not even
               * type-check, and the owner found all three by looking at his own screen**: a
               * `thin`/`deep`/`barren` word, a character count, and which of twelve subjects was
               * allowed to have buttons. See that module for what each of them cost.
               */
              const view = subjectView(subject, { running: working });
              /*
               * The reason a subject is not in the plan, and it is never allowed to be nothing.
               * Synthesis's own sentence when there is one — never paraphrased, it is about this
               * learner's own subject — and a sentence of ours below that. "Not in your plan"
               * with silence under it is the bug the whole report exists to remove.
               *
               * **What no longer reaches it is the research pass's `missing`** (F-158). The API
               * used to fall back to it for a `barren` subject; it is written for whoever builds
               * the curriculum and it said so out loud on this screen. See `coverageFor`.
               */
              const why = verdict?.why || (verdict?.whyKey ? t(verdict.whyKey) : '');

              return (
                <li key={subject.subjectId}>
                  <p className="meta">
                    <strong>{subject.title}</strong>{' '}
                    {/*
                      A badge only while the subject is still on its way, or when something
                      about *us* stopped it (F-158). A researched subject gets none: the three
                      words that used to go here — researched, little found, nothing found —
                      were a verdict on whether enough was found, and the threshold for enough
                      is entirely subject-dependent. The sources line below says what it rests
                      on and the abstract says what it covers; the learner judges.
                    */}
                    {view.badge && (
                      <span className={`pill pill-${view.badge.tone}`}>{t(view.badge.key)}</span>
                    )}
                    {/* Not an error tint, deliberately — `.pill-off` is the quiet outline the
                        plan screen uses for a subject that is switched off. A subject that did
                        not reach the plan is a thing to know and act on, not a fault report. */}
                    {verdict?.inPlan === false && (
                      <span className="pill pill-off">{t('cov.notInPlan')}</span>
                    )}
                  </p>
                  <p className="muted small">{subject.abstract}</p>

                  {/* One bar per subject, which is the level the work actually happens at: a
                      wave researches six subjects and the overall number moves once, while
                      these six move independently and say which of them is in flight. */}
                  <progress value={subjectFraction(subject)} max={1} />

                  {(view.sources || (subject.attempts ?? 0) > 1) && (
                    <p className="muted small">
                      {/*
                        What this subject rests on, in sources (F-158).

                        It said characters until F-158 and the count was the complaint: *"number
                        of characters is quite irrelevant, can we do number of sources?"* Four
                        sentences rather than a number, because a stored nought and no stored
                        count at all are different facts — and every row in the table predates
                        the counter on the day this ships. `sourcesLine` decides which.
                      */}
                      {view.sources
                        ? t(view.sources.key).replace(
                            '{sources}',
                            view.sources.count.toLocaleString(locale),
                          )
                        : ''}
                      {/*
                        The second attempt, said out loud (F-147).

                        A subject that has been started twice has usually sat at *reading* for
                        twice as long as its neighbours, and the row has counted it since F-126
                        while the screen said nothing — so the one honest explanation for the
                        slowest thing on the page was on the row all along. Only ever shown
                        above one: "attempt 1" is every subject and is noise.
                      */}
                      {(subject.attempts ?? 0) > 1
                        ? `${view.sources ? ' · ' : ''}${t('res.attempts').replace(
                            '{attempts}',
                            String(subject.attempts),
                          )}`
                        : ''}
                    </p>
                  )}

                  {/*
                   * **The subject's `missing` was printed here until F-158, and it is gone.**
                   *
                   * F-147 put it on the screen on the reasoning that it is the most actionable
                   * sentence in a run — which page from a book the learner owns would close the
                   * gap. What it actually is, is a note the research pass writes for whoever
                   * builds the curriculum out of it, and it says so in its own words. Leo,
                   * 2026-09-19, reading his own plan: *"Scope warning for whoever builds the
                   * curriculum from this: the web tooling stopped returning results after a
                   * single query…"* — on the carousel of a new learner's first screen.
                   *
                   * It stays on the row. The console reads it, and so does the coverage report
                   * that an operator runs. Nothing a learner can see renders it.
                   */}

                  {verdict?.inPlan === true && (
                    <p className="muted small">
                      {verdict.nodes === 1
                        ? t('cov.inPlan.one')
                        : t('cov.inPlan.many').replace('{nodes}', String(verdict.nodes))}
                    </p>
                  )}

                  {verdict?.inPlan === false && why && <p className="muted small">{why}</p>}

                  {/*
                   * **Both actions, on every subject, whatever it found** (F-158).
                   *
                   * Leo, 2026-09-19: *"researched doesn't show those buttons, there is no reason
                   * to believe i wont want to add my own material to a researched subject."* The
                   * gate this replaces was three conditions deep — a coverage verdict had to
                   * exist, it had to list a remedy, and the state had to be in a `DEEPENABLE`
                   * set — and the subject it excluded was the one that had gone best. A second
                   * block below it offered one button of the two whenever the report could not
                   * be read, which is every subject before a plan is built.
                   *
                   * One condition is left and it is about the run, not the subject: a pass in
                   * flight would refuse both. See `actionsShown`.
                   *
                   * `.actions-inline-small` rather than `.actions-inline`: twelve rows carrying
                   * two of the app's full-size primary-shaped buttons is what *"the add a page
                   * of your own and go deeper buttons are huge"* was about. They stay at the
                   * app's 44-pixel thumb-target habit and lose the weight and the padding.
                   */}
                  {view.actions && (
                    <div className="actions-inline actions-inline-small">
                      {/* Never `primary`. The thing to press on this screen is the fold or the
                          upload panel itself; these are per-subject. */}
                      <button type="button" className="button button-small" onClick={goToUpload}>
                        {t('cov.remedy.upload')}
                      </button>
                      <button
                        type="button"
                        className="button button-small"
                        disabled={deepening !== undefined}
                        onClick={() => void goDeeper(subject)}
                      >
                        {/* "Go deeper" is the wrong invitation for a subject nothing was found
                            for: there is nothing to go deeper into. The second look is the same
                            call either way; the words say which situation the learner is in, and
                            `againLabelFor` decides from the subject's own state rather than from
                            a coverage report that may not have been read. */}
                        {deepening === subject.subjectId
                          ? t('res.deeper.working')
                          : t(view.again)}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {run.error && (
            <p className="problem" role="alert">
              {run.error}
            </p>
          )}
        </section>
      )}

      {documents && documents.length > 0 && (
        <section className="panel">
          <details className="history" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
            <summary>
              <h2>{t('res.title')}</h2>
              {/*
                How many documents are listed, and not how many characters are in them (F-158).

                **The character total under this heading counted something other than what the
                heading names**, and the owner read it against the subject rows above: *"what
                research found says 2730 characters but each section already has dozens of
                thousands."* `readResearch` lists at most five documents — `.slice(0, 5)`, a
                payload bound, because each one may be 120,000 characters — so on a run of twelve
                subjects this summed five of them, under a heading that reads as the whole of what
                research found.

                **That bound does not on its own explain 2,730**, and a review was right to say
                so: five of that objective's sections sum to well over twenty thousand. Whatever
                else was behind the figure — another objective, a listing that returned less than
                five, a document written before the pass that filled it — the heading was summing
                a list it had bounded and calling the total "what the research found", and that is
                fixable here. What is not fixable here is a sum that disagrees with the rows
                above it, so this stops trying to be one.

                Counting the documents says something this panel can actually promise, and it
                says out loud that they are the most recent ones rather than all of them.

                Two character counts survive on this screen and neither is this one: each
                document's own length, a few lines below, which describes the exact block of text
                under it, and `pulse.findings` on the plan panel, which is the run's true total
                across every subject. The count that had to go is the one that was a partial sum
                wearing a total's heading.
              */}
              <span className="muted small">
                {documents.length === 1
                  ? t('res.docs.one')
                  : t('res.docs.many').replace('{documents}', String(documents.length))}
              </span>
            </summary>

            <p className="muted">{t('res.hint')}</p>

            {documents.map((doc) => (
              <article key={doc.key}>
                <p className="meta small">
                  {doc.title} · {when.format(new Date(doc.producedAt))} · {doc.origin} ·{' '}
                  {doc.characters.toLocaleString(locale)} {t('res.characters')}
                </p>
                {/* Deliberately raw, in a scrolling block, rather than rendered as Markdown.
                    The document's value here is that it is the exact text the curriculum was
                    built from — including the tables and the inline URLs — and a renderer that
                    swallowed a malformed table or a citation would be hiding the thing worth
                    checking. */}
                <pre className="extracted research">
                  {doc.body}
                  {doc.truncated ? `\n\n… ${t('res.truncated')}` : ''}
                </pre>
              </article>
            ))}
          </details>
        </section>
      )}
    </>
  );
}

/**
 * How far through its own life one subject is.
 *
 * **Duplicated from `SUBJECT_PROGRESS` in `@nabu/shared`, deliberately and unhappily.** The
 * shared barrel re-exports `keys.ts`, which imports `node:crypto`; a value import from it
 * would pull that into a browser bundle, and reaching past the barrel into a build artefact
 * would be worse. Every other consumer of these numbers gets the shared table — this one
 * cannot, so it gets a copy with this note on it.
 *
 * The `overall` fraction is *not* duplicated: it comes from the API, computed once, precisely
 * so the browser cannot hold a second opinion about the number that matters most.
 */
function subjectFraction(subject: ResearchSubject): number {
  switch (subject.state) {
    case 'outlined':
      return 0.25;
    case 'queued':
      return 0.4;
    case 'researching':
      return 0.6;
    default:
      return 1;
  }
}
